import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { Permission, User, UserGroup } from '@restore/services-lib';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { GET_USERS } from 'graphql/users/queries/getUsers.gql';
import { GET_PERMISSIONS } from 'graphql/users/queries/getPermissions.gql';
import { UPDATE_USER_GROUP } from 'graphql/users/mutations/updateUserGroup.gql';
import { UPDATE_USER } from 'graphql/users/mutations/updateUser.gql';
import { CREATE_USER } from 'graphql/users/mutations/createUsers.gql';
import { TOGGLE_USER_ACTIVE } from 'graphql/users/mutations/toggleUserActive.gql';
import { CREATE_USER_GROUP } from 'graphql/users/mutations/createUserGroup.gql';
import { DELETE_USER_GROUP } from 'graphql/users/mutations/deleteUserGroup.gql';
import { GET_USER_GROUPS } from 'graphql/users/queries/getUserGroups.gql';
import { useAuthContext } from 'context/auth.context';
import { useSnackbarContext } from 'context/snackbar.context';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import { getUsersName } from 'utils/formatting';
import { hasPermissions } from 'config/ability';
import { EXPORT_USERS } from 'graphql/users/mutations/exportUsers.gql';

export interface CreateUserInput {
  firstName: string;
  lastName: string;
  email: string;
  userGroupIds: number[];
  tagIds: number[];
}

export interface UpdateUserInput extends CreateUserInput {
  id: number;
}

export interface CreateUserGroupInput {
  name: string;
  displayColour: string;
  permissionIds: number[];
}

export interface UpdateUserGroupInput extends CreateUserGroupInput {
  id: number;
}

export interface UsersFilter {
  tenantId?: number;
  name?: string;
  email?: string;
  isActive?: boolean;
  userGroupIds?: number[];
  tagIds?: number[];
}

interface UsersContextInterface {
  // users
  users: User[];
  setUsers: (users: User[]) => void;
  addUser: (user: CreateUserInput) => void;
  usersFilter: UsersFilter;
  setUsersFilter: (filter: UsersFilter) => void;
  editUser: (user: UpdateUserInput) => void;
  toggleUserActive: (userId: number) => void;
  getUserById: (userId?: number) => User | undefined;
  getUserDropdownSelectOptionsByIds: (userIds: number[]) => DropdownOption[];
  loadingUsers: boolean;
  errorUsers?: ApolloError;
  // user groups
  userGroups: UserGroup[];
  setUserGroups: (userGroups: UserGroup[]) => void;
  addUserGroup: (userGroup: CreateUserGroupInput) => void;
  editUserGroup: (userGroup: UpdateUserGroupInput) => Promise<void>;
  removeUserGroup: (id: number) => void;
  getUserGroupById: (id: number) => UserGroup | undefined;
  getUserGroupDropdownSelectOptionsByIds: (campaignIds: number[]) => DropdownOption[];
  exportCsv: () => Promise<void>;
  loadingUserGroups: boolean;
  loadingAddUserGroup: boolean;
  errorUserGroups?: ApolloError;
  // permissions
  permissions: Permission[];
  setPermissions: (permissions: Permission[]) => void;
  loadingPermissions: boolean;
  errorPermissions?: ApolloError;
}

const defaultContext: UsersContextInterface = {
  // users
  users: [],
  setUsers: () => {},
  usersFilter: {},
  setUsersFilter: () => {},
  addUser: async () => {},
  editUser: async () => {},
  toggleUserActive: async () => {},
  getUserById: () => undefined,
  getUserDropdownSelectOptionsByIds: () => [],
  exportCsv: async () => {},
  loadingUsers: true,
  // user groups
  userGroups: [],
  setUserGroups: async () => {},
  addUserGroup: async () => {},
  editUserGroup: async () => {},
  removeUserGroup: async () => {},
  getUserGroupById: () => undefined,
  getUserGroupDropdownSelectOptionsByIds: () => [],
  loadingUserGroups: true,
  loadingAddUserGroup: false,
  // permissions
  permissions: [],
  setPermissions: async () => {},
  loadingPermissions: false,
};

const UsersContext = createContext<UsersContextInterface>(defaultContext);

export const UsersContextProvider = ({ children }: { children: ReactNode }) => {
  const { ability, userTenantId } = useAuthContext();
  const [users, setUsers] = useState<User[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loadingAddUserGroup, setLoadingAddUserGroup] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(hasPermissions(ability, 'read', 'User'));
  const [loadingUserGroups, setLoadingUserGroups] = useState(
    hasPermissions(ability, 'read', 'UserGroup')
  );
  const [createUser] = useMutation(CREATE_USER);
  const [updateUser] = useMutation(UPDATE_USER);
  const [toggleUserActiveMutation] = useMutation(TOGGLE_USER_ACTIVE);
  const [createUserGroup] = useMutation(CREATE_USER_GROUP);
  const [updateUserGroup] = useMutation(UPDATE_USER_GROUP);
  const [deleteUserGroup] = useMutation(DELETE_USER_GROUP);
  const [exportUsersCsv] = useMutation(EXPORT_USERS);
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();
  const [usersFilter, setUsersFilter] = useState<UsersFilter>({
    tenantId: userTenantId,
  });

  useMemo(() => {
    setUsersFilter({
      ...usersFilter,
      tenantId: userTenantId,
    });
  }, [userTenantId, ability]);

  // fetch users
  const { error: errorUsers, refetch: refetchUsers } = useQuery(GET_USERS, {
    skip: !userTenantId || !usersFilter.tenantId || !hasPermissions(ability, 'read', 'User'),
    variables: {
      usersFilter,
    },
    onCompleted: (data) => {
      setUsers(data.users);
      setLoadingUsers(false);
    },
  });

  // create user
  const addUser = async (input: CreateUserInput) => {
    await createUser({
      variables: {
        input: {
          tenantId: userTenantId,
          ...input,
        },
      },
    }).then(
      (result) => {
        displayApiResult(result.data.createUser, 'Created successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );

    await refetchUsers();
  };

  // update user
  const editUser = async (input: UpdateUserInput) => {
    await updateUser({
      variables: {
        input,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.updateUser, 'Updated successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );

    await refetchUsers();
  };

  // toggle user's active status
  const toggleUserActive = async (id: number) => {
    await toggleUserActiveMutation({
      variables: {
        id: id,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.toggleUserActive);
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );

    await refetchUsers();
  };

  // get user by id
  const getUserById = (id?: number) => {
    return id ? users.find((user) => user.id === id) : undefined;
  };

  // get user dropdown select options from an array of user ids
  const getUserDropdownSelectOptionsByIds = (userIds: number[]) => {
    const selectedUsers = userIds.map((id) => getUserById(id));
    const selectedUserDropdownOptions: DropdownOption[] = [];
    selectedUsers?.forEach(
      (user) => user && selectedUserDropdownOptions.push({ name: getUsersName(user), id: user.id })
    );
    return selectedUserDropdownOptions;
  };

  // fetch user groups
  const { error: errorUserGroups, refetch: refetchUserGroups } = useQuery(GET_USER_GROUPS, {
    skip: !userTenantId || !hasPermissions(ability, 'read', 'UserGroup'),
    variables: {
      tenantId: userTenantId,
    },
    onCompleted: (data) => {
      setUserGroups(data.userGroups);
      setLoadingUserGroups(false);
    },
  });

  // create user group
  const addUserGroup = async (input: CreateUserGroupInput) => {
    setLoadingAddUserGroup(true);
    await createUserGroup({
      variables: {
        input: {
          ...input,
          tenantId: userTenantId,
        },
      },
    }).then(
      (result) => {
        displayApiResult(result.data.createUserGroup, 'Created successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );

    setLoadingAddUserGroup(false);
    await refetchUserGroups();
  };

  const removeUserGroup = async (id: number) => {
    await deleteUserGroup({
      variables: {
        id,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.removeUserGroup, 'Deleted successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    await refetchUserGroups();
  };

  // get user group by id
  const getUserGroupById = (id: number) => {
    return userGroups.find((group) => group.id === id);
  };

  // edit user group
  const editUserGroup = async ({
    id,
    name,
    displayColour,
    permissionIds,
  }: UpdateUserGroupInput) => {
    await updateUserGroup({
      variables: {
        input: {
          id,
          name,
          displayColour,
          permissionIds,
        },
      },
    }).then(
      (result) => {
        displayApiResult(result.data.updateUserGroup, 'Updated successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );

    await refetchUserGroups();
  };

  // get user group dropdown select options from an array of user ids
  const getUserGroupDropdownSelectOptionsByIds = (userGroupIds: number[]) => {
    const selectedUserGroups = userGroupIds.map((id) => getUserGroupById(id));
    const selectedUserGroupDropdownOptions: DropdownOption[] = [];
    selectedUserGroups?.forEach(
      (userGroup) =>
        userGroup &&
        selectedUserGroupDropdownOptions.push({ name: userGroup.name, id: userGroup.id })
    );
    return selectedUserGroupDropdownOptions;
  };

  const exportCsv = async () => {
    await exportUsersCsv({
      variables: {
        input: usersFilter,
      },
    }).then(
      (result) => {
        const csvDataEncoded = result.data.exportUsers.file.encoding;
        const dataUrl = `data:${result.data.exportUsers.file.mimetype};base64,${csvDataEncoded}`;

        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = result.data.exportUsers.file.filename;
        link.click();

        displayApiResult(result.data.exportUsers, 'CSV Created Successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
  };

  // fetch permissions
  const { loading: loadingPermissions, error: errorPermissions } = useQuery(GET_PERMISSIONS, {
    skip: !userTenantId || !hasPermissions(ability, 'read', 'UserGroup'),
    onCompleted: (data) => {
      setPermissions(data.permissions);
    },
    onError: (error) => {
      displaySnackbarMessage(error.message, 'error');
    },
  });

  return (
    <UsersContext.Provider
      value={{
        // users
        users,
        setUsers,
        usersFilter,
        setUsersFilter,
        addUser,
        editUser,
        toggleUserActive,
        getUserById,
        getUserDropdownSelectOptionsByIds,
        loadingUsers,
        errorUsers,
        //user groups
        userGroups,
        setUserGroups,
        addUserGroup,
        editUserGroup,
        removeUserGroup,
        getUserGroupById,
        getUserGroupDropdownSelectOptionsByIds,
        exportCsv,
        loadingUserGroups,
        loadingAddUserGroup,
        errorUserGroups,
        // permissions
        permissions,
        setPermissions,
        loadingPermissions,
        errorPermissions,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

export const useUsersContext = () => useContext(UsersContext);
